import { useContext } from 'react';

import { TooltipContext, type TooltipContextType } from '@visx/xychart';

/**
 * Hook to access cursor state and control functions from outside the chart.
 *
 * Provides access to the current cursor position, hovered data point, and functions
 * to programmatically show, hide, or update the cursor/tooltip. Requires CursorProvider
 * to be rendered as an ancestor component.
 *
 * @returns Object containing:
 *   - data: The datum at the cursor position
 *   - index: Index of the datum in the data array
 *   - nearestKey: The data series key of the nearest point
 *   - showCursor: Function to show the cursor
 *   - hideCursor: Function to hide the cursor
 *   - updateCursor: Function to update cursor position
 *
 * @example
 * ```tsx
 * function ExternalValue() {
 *   const { data, hideCursor } = useCursor();
 *
 *   return (
 *     <div>
 *       Current value: {data?.value}
 *       <button onClick={() => hideCursor()}>Hide cursor</button>
 *     </div>
 *   );
 * }
 *
 * function MyChart() {
 *   return (
 *     <CursorProvider>
 *       <ExternalValue />
 *       <XYChart width={500} height={300}>
 *         <AreaSeries data={data} dataKey="series1" xAccessor={d => d.x} yAccessor={d => d.y} />
 *         <Cursor formatDataPointLabel={(d) => `${d.value}`} />
 *       </XYChart>
 *     </CursorProvider>
 *   );
 * }
 * ```
 */
export const useCursor = <TData extends object>() => {
  const visxTooltipContext = useContext<TooltipContextType<TData> | null>(TooltipContext);
  return {
    data: visxTooltipContext?.tooltipData?.nearestDatum?.datum,
    index: visxTooltipContext?.tooltipData?.nearestDatum?.index,
    nearestKey: visxTooltipContext?.tooltipData?.nearestDatum?.key,
    showCursor: visxTooltipContext?.showTooltip,
    hideCursor: visxTooltipContext?.hideTooltip,
    updateCursor: visxTooltipContext?.updateTooltip,
  };
};
